import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Banknote,
  Bell,
  Building2,
  CreditCard,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  MapPin,
  Pencil,
  Receipt,
  ShoppingCart,
  StickyNote,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useClient, useClientCounts } from "@/features/clients/api";
import { CLIENT_TYPES, formatMoney, labelFor } from "@/features/clients/constants";
import { useClientId } from "@/features/clients/detail/use-client-id";
import { cn } from "@/lib/utils";

type CountKey = "contacts" | "addresses" | "notes" | "reminders" | "files";

const NAV = [
  { key: "profile", label: "Profile", to: "/clients/$clientId/profile", icon: Building2, live: true },
  {
    key: "contacts",
    label: "Contacts",
    to: "/clients/$clientId/contacts",
    icon: Users,
    live: true,
    countKey: "contacts" as const,
  },
  {
    key: "addresses",
    label: "Addresses",
    to: "/clients/$clientId/addresses",
    icon: MapPin,
    live: true,
    countKey: "addresses" as const,
  },
  {
    key: "notes",
    label: "Notes",
    to: "/clients/$clientId/notes",
    icon: StickyNote,
    live: true,
    countKey: "notes" as const,
  },
  {
    key: "reminders",
    label: "Reminders",
    to: "/clients/$clientId/reminders",
    icon: Bell,
    live: true,
    countKey: "reminders" as const,
  },
  {
    key: "files",
    label: "Files",
    to: "/clients/$clientId/files",
    icon: FolderOpen,
    live: true,
    countKey: "files" as const,
  },
  { key: "quotations", label: "Quotations", to: "/clients/$clientId/quotations", icon: FileText, live: true },
  { key: "orders", label: "Orders", to: "/clients/$clientId/orders", icon: ShoppingCart, live: true },
  { key: "invoices", label: "Invoices", to: "/clients/$clientId/invoices", icon: Receipt, live: true },
  { key: "payments", label: "Payments", to: "/clients/$clientId/payments", icon: Banknote, live: true },
  {
    key: "credit-notes",
    label: "Credit notes",
    to: "/clients/$clientId/credit-notes",
    icon: CreditCard,
    live: true,
  },
  {
    key: "statement",
    label: "Statement",
    to: "/clients/$clientId/statement",
    icon: FileSpreadsheet,
    live: true,
  },
] as const;

export function ClientDetailLayout() {
  const clientId = useClientId();
  const navigate = useNavigate();
  const { data: client, isLoading, error } = useClient(clientId);
  const { data: counts } = useClientCounts(clientId);

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center text-sm text-muted-foreground">
        Loading client…
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] flex-col items-center justify-center gap-3">
        <p className="text-sm text-destructive">{(error as Error)?.message ?? "Client not found"}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => void navigate({ to: "/clients" })}>
          Back to clients
        </Button>
      </div>
    );
  }

  return (
    <div className="-mx-4 -my-3 flex h-[calc(100vh-3rem)] min-h-0 flex-col bg-surface md:-mx-6 md:-my-4">
      {/* Record header — matches Yantralogic customer title strip */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Back to clients"
            onClick={() => void navigate({ to: "/clients" })}
          >
            <ArrowLeft />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-sm font-semibold tracking-[-0.01em]">
                <span className="font-mono text-muted-foreground">#{client.code}</span>
                <span className="mx-1.5 text-muted-foreground/40">·</span>
                {client.name}
              </h1>
              <Badge variant={client.is_active ? "success" : "secondary"}>
                {client.is_active ? "Active" : "Inactive"}
              </Badge>
              <Badge variant="outline">{labelFor(CLIENT_TYPES, client.client_type)}</Badge>
            </div>
            <p className="truncate text-2xs text-muted-foreground">
              {[client.city, client.state].filter(Boolean).join(", ")}
              {client.gst_no ? ` · ${client.gst_no}` : ""}
              {client.outstanding_balance > 0
                ? ` · To collect ${formatMoney(client.outstanding_balance)}`
                : ""}
              {client.overdue_balance > 0
                ? ` · Overdue ${formatMoney(client.overdue_balance)}`
                : ""}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: "/clients/$clientId/edit", params: { clientId } })}
          >
            <Pencil />
            Edit
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Secondary vertical nav — customer hub modules */}
        <aside className="flex w-48 shrink-0 flex-col border-r border-border bg-card">
          <ScrollArea className="flex-1 py-2">
            <nav className="space-y-px px-1.5">
              {NAV.map((item) => {
                const Icon = item.icon;
                const count =
                  "countKey" in item && counts ? counts[item.countKey as CountKey] : undefined;
                return (
                  <Link
                    key={item.key}
                    to={item.to}
                    params={{ clientId }}
                    className={cn(
                      "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs transition-colors",
                      item.live
                        ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                        : "cursor-default text-muted-foreground/40",
                    )}
                    activeProps={{
                      className:
                        "bg-primary/10 font-medium text-primary hover:bg-primary/10 hover:text-primary",
                    }}
                    onClick={(e) => {
                      if (!item.live) e.preventDefault();
                    }}
                  >
                    <Icon className="size-3.5 shrink-0" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {typeof count === "number" && count > 0 ? (
                      <span className="rounded bg-primary/15 px-1.5 text-[10px] font-medium tabular-nums text-primary">
                        {count}
                      </span>
                    ) : null}
                    {!item.live ? (
                      <span className="text-[9px] uppercase tracking-wide opacity-70">Soon</span>
                    ) : null}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>
        </aside>

        {/* Content panel */}
        <div className="min-w-0 flex-1 overflow-auto p-3 md:p-4">
          <div className="min-h-full rounded-lg border border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}